/**
 * Cloned from Application Engine:
 * `ApplicationEngineTopbarFilterCompany.tsx`
 * — URL/search-params replaced with controlled `value` + `onCommit` props.
 */
import React, { useCallback, useMemo, useState } from 'react'
import { Select } from 'antd'

import {
  useCompany,
  convertToCompanySelectOptions,
} from 'modules/client-service'

export interface VcnManagementCompanySelectProps {
  value: string[]
  onCommit: (clientPublicKeys: string[]) => void
  isDisabled?: boolean
  /** When true, every change commits immediately (use in filter drawer). */
  immediateCommit?: boolean
  style?: React.CSSProperties
}

export function VcnManagementCompanySelect({
  value = [],
  onCommit,
  isDisabled = false,
  immediateCommit = false,
  style,
}: VcnManagementCompanySelectProps) {
  const { queryCompanies } = useCompany()
  const { data: companiesData, isLoading: isLoadingCompanies } =
    queryCompanies()
  const [pendingValue, setPendingValue] = useState<string[] | null>(null)

  const displayValue = pendingValue ?? value

  const commitValue = useCallback(
    (next: string[]) => {
      onCommit(next.length ? next : [])
      setPendingValue(null)
    },
    [onCommit]
  )

  const handleChange = useCallback(
    (next: string[]) => {
      if (immediateCommit || next.length === 0) {
        commitValue(next)
        return
      }
      setPendingValue(next)
    },
    [immediateCommit, commitValue]
  )

  const handleDropdownVisibleChange = useCallback(
    (open: boolean) => {
      if (!open && pendingValue !== null) {
        commitValue(pendingValue)
      }
    },
    [pendingValue, commitValue]
  )

  const companyOptions = useMemo(
    () =>
      convertToCompanySelectOptions(companiesData?.data || [], {
        valueKey: 'id',
      }),
    [companiesData?.data]
  )

  return (
    <Select
      mode="multiple"
      maxTagCount="responsive"
      showSearch
      allowClear
      placeholder="All Companies"
      loading={isLoadingCompanies || isDisabled}
      disabled={isDisabled}
      value={displayValue}
      onChange={handleChange}
      onDropdownVisibleChange={handleDropdownVisibleChange}
      onBlur={() => {
        if (pendingValue !== null) {
          commitValue(pendingValue)
        }
      }}
      filterOption={(input, option) =>
        String(option?.label).toLowerCase().includes(input.toLowerCase())
      }
      options={companyOptions}
      size="large"
      style={style ?? { minWidth: 200, maxWidth: 360, flexShrink: 0 }}
    />
  )
}
